import React, { useEffect, useState, useRef } from "react";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  MessageSeparator,
  Avatar,
} from "@chatscope/chat-ui-kit-react";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";

import { io } from "socket.io-client";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
// import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { Dayjs } from "dayjs";
import { Box, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { ISmsMessage } from "src/types/message";
import {
  GetAllSmsByEntityId,
  PostSms,
} from "src/redux/features/messages/message-slice";

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const SmsView = ({
  entityId,
  entityType,
}: {
  entityId: string;
  entityType: string;
}) => {
  //#region State and Dispatch
  const dispatch = useAppDispatch();
  const [messages, setMessages] = useState<ISmsMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  // const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
  //   null,
  //   null,
  // ]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  //handles search filter
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");
  const socketRef = useRef<any>(null);
  const messageListRef = useRef<any>(null);
  const orgProfile = useAppSelector((state) => state.organizationSlice.orgData);
  const smsHistory = useAppSelector((state) => state.messageSlice.smsHistory);
  // const smsHistory = useAppSelector
  //#endregion

  //#region functions
  const handleSend = async () => {
    if (!inputValue.trim() || !orgProfile.id) return;

    // const newMsg: ISmsMessage = {
    //   id: `${Date.now()}`,
    //   message: inputValue,
    //   type: "sms",
    //   fromUser: {
    //     firstName: "You",
    //     lastName: "",
    //     email: "",
    //     profilePictureUrl: null,
    //   },
    //   fromName: "You",
    //   entityId,
    //   entityType,
    //   createdOn: new Date().toISOString(),
    //   updatedOn: new Date().toISOString(),
    //   smsStatus: "outbound",
    // };

    // setMessages((prev) => [...prev, newMsg]);
    setInputValue("");
    dispatch(
      PostSms({
        Body: inputValue,
        entityId,
        entityType,
        Direction: "outbound",
        DateCreated: new Date().toISOString(),
        descope_tenantId: orgProfile.descope_tenantId,
      })
    );
  };
  function useDebounce(cb: (...args: any[]) => void, delay: number) {
    const timeoutId = useRef<ReturnType<typeof setTimeout>>();

    return function (...args: any[]) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      timeoutId.current = setTimeout(() => cb(...args), delay);
    };
  }

  const debouncedChange = useDebounce((inputValue: string) => {
    setDebouncedSearchText(inputValue);
  }, 500);

  const handleSmsSearch = (smsHistory: ISmsMessage[]) => {
    return smsHistory.filter((item) =>
      item.message.toLowerCase().includes(debouncedSearchText.toLowerCase())
    );
  };

  const renderMessages = () => {
    return handleSmsSearch(messages)?.map((msg, index) => {
      const currentDate = new Date(msg.createdOn);
      const prevDate =
        index > 0 ? new Date(messages[index - 1]?.createdOn || "") : null;

      const isNewDay =
        !prevDate || currentDate.toDateString() !== prevDate.toDateString();

      let separatorLabel = "";
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      if (isNewDay) {
        if (currentDate.toDateString() === today.toDateString()) {
          separatorLabel = "Today";
        } else if (currentDate.toDateString() === yesterday.toDateString()) {
          separatorLabel = "Yesterday";
        } else {
          separatorLabel = currentDate.toLocaleDateString();
        }
      }

      return (
        <React.Fragment key={msg.id}>
          {isNewDay && (
            <MessageSeparator
              style={{ justifyContent: "space-around", color: "#495057" }}
            >
              {separatorLabel}
            </MessageSeparator>
          )}
          <Message
            model={{
              message: msg.message,
              direction:
                msg.direction === "inbound" ? "outgoing" : "incoming", // reversed for positioning
              position: "single",
            }}
          >
            <Avatar
              className={
                msg.direction === "inbound"
                  ? "custom-avatar inbound-avatar"
                  : "custom-avatar outbound-avatar"
              }
            >
              {msg.fromUser?.firstName?.[0]}
              {msg.fromUser?.lastName?.[0]}
            </Avatar>
            <Message.Footer
              sentTime={formatTime(msg.createdOn)}
              // sender={msg.fromName}
              // style={
              //   msg.direction === "inbound"
              //     ? { display: "block" }
              //     : { justifyContent: "flex-end" }
              // }
            />
          </Message>
        </React.Fragment>
      );
    });
  };
  //#endregion

  //#region Effects
  // Initialize socket once
  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_SOCKET_IO, {
      transports: ["websocket"],
      withCredentials: true,
    });

    const socket = socketRef.current;

    socket.on("connect", () => {
      setIsConnected(true);
      if (orgProfile.descope_tenantId) {
        socket.emit("newUser", { orgId: orgProfile.descope_tenantId });
      }
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // // Emit newUser on orgProfile change
  // useEffect(() => {
  //   if (socketRef.current && orgProfile.descope_tenantId && isConnected) {
  //     socketRef.current.emit("newUser", { orgId: orgProfile.descope_tenantId });
  //   }
  // }, [orgProfile.descope_tenantId, isConnected]);

  // Handle incoming messages
  useEffect(() => {
    if (!entityId || !entityType || !socketRef.current || !isConnected) return;

    const socket = socketRef.current;
    const eventKey =
      entityType.toLowerCase() === "client"
        ? `Client-${entityId}`
        : `Provider-${entityId}`;

    const handler = (payload: any) => {
      const newMsg: ISmsMessage = {
        id: `${Date.now()}`,
        message: payload.message,
        type: "sms",
        fromUser: {
          firstName: payload.fromUser.firstName,
          lastName: payload.fromUser.lastName,
          email: payload.fromUser.email,
          profilePictureUrl: payload.fromUser.profilePictureUrl || null,
        },
        fromName: "You",
        entityId,
        entityType,
        createdOn: payload.timestamp,
        updatedOn: payload.timestamp,
        direction: payload.Direction,
      };
      setMessages((prev) => [...prev, newMsg]);
    };

    socket.on(eventKey, handler);

    return () => {
      socket.off(eventKey, handler);
    };
  }, [entityId, entityType, isConnected]);
  useEffect(() => {
    dispatch(
      GetAllSmsByEntityId({
        entityId,
        entityType,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    setMessages(smsHistory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsHistory.length]);
  //#endregion

  return (
    <Box className="sms-View">
      <Box className="sms-filter">
        <Typography className="filter-label">Search</Typography>
        <Box className="search-box">
          <SearchIcon className="search-icon" color="primary" />
          <TextField
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              debouncedChange(e.target.value);
            }}
            id="outlined-basic"
            label="search texts"
            variant="outlined"
            className="search-filter"
            fullWidth
            size="small"
          />
        </Box>
      </Box>
      <Box className="sms-filter date-range-filter">
        <Typography className="filter-label">Date Range</Typography>
        <Box className="date-range-box">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="start date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e);
                if (e && endDate) {
                  dispatch(
                    GetAllSmsByEntityId({
                      entityId,
                      entityType,
                      from: e.toISOString(),
                      to: endDate.toISOString(),
                    })
                  );
                }
              }}
            />
            <DatePicker
              label="end date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e);
                if (startDate && e) {
                  dispatch(
                    GetAllSmsByEntityId({
                      entityId,
                      entityType,
                      from: startDate.toISOString(),
                      to: e.toISOString(),
                    })
                  );
                }
              }}
            />

            {/* <DateRangePicker
            value={dateRange}
            onChange={(newValue) => {
              setDateRange(newValue);

              if (newValue[0] && newValue[1]) {
                dispatch(
                  GetAllSmsByEntityId({
                    entityId,
                    from: newValue[0].toISOString(),
                    to: newValue[1].toISOString(),
                  })
                );
              }
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                fullWidth: true,
              },
            }}
          /> */}
          </LocalizationProvider>
        </Box>
      </Box>

      <MainContainer className="sms-chat-container">
        <ChatContainer>
          <MessageList
            scrollBehavior="auto"
            typingIndicator={null}
            ref={messageListRef}
          >
            {renderMessages()}
          </MessageList>
          <MessageInput
            placeholder="Type message here"
            value={inputValue}
            onChange={(val) => setInputValue(val)}
            onSend={handleSend}
            attachButton={false}
          />
        </ChatContainer>
      </MainContainer>
    </Box>
  );
};

export default SmsView;
